// Epic 8 follow-on — TanStack mutation for
// POST /api/v1/creators/signature-upload-url.
//
// Mirrors useMintImageUploadUrl but for the SEPARATE creator-signature
// blob family (PNG/WebP). Returns a SAS URI + required headers + the
// canonical blob_path so the browser PUTs the file directly. Fresh
// idempotency key per call so SPA-side retries are replay-safe.

import { useMutation } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type {
  MintImageUploadUrlRequest,
  MintImageUploadUrlResponse,
  MintImageUploadUrlError,
} from "./useMintImageUploadUrl";

async function postMint(
  body: MintImageUploadUrlRequest,
  idempotencyKey: string,
): Promise<MintImageUploadUrlResponse> {
  const response = await fetch("/api/v1/creators/signature-upload-url", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: MintImageUploadUrlError = {
      code: "SIGNATURE_UPLOAD_URL_MINT_FAILED",
      message: `Mint failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: MintImageUploadUrlResponse;
  };
  return envelope.data;
}

export function useMintSignatureUploadUrl() {
  return useMutation<
    MintImageUploadUrlResponse,
    MintImageUploadUrlError,
    MintImageUploadUrlRequest
  >({
    mutationFn: (body) => postMint(body, crypto.randomUUID()),
  });
}
